/**
 * Shared building blocks for the marketing pages (Home, Features, Pricing, FAQ).
 *
 * @module pages/marketing/shared
 */

import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

/** First-employee monthly price (all-inclusive core). */
export const PRICE_FIRST = 22;
/** Per-additional-employee monthly price. */
export const PRICE_ADDL = 7;

export function CheckIcon(): ReactElement {
  return (
    <svg className="h-5 w-5 flex-none text-brand-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** A capability column (Payroll / Tax Filing / Contractors). */
export function PillarCard({ title, body }: { title: string; body: string }): ReactElement {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
    </div>
  );
}

/** Alternating text/visual feature row. */
export function FeatureRow({
  eyebrow,
  title,
  body,
  bullets,
  reverse,
}: {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  reverse?: boolean;
}): ReactElement {
  return (
    <div className={`grid items-center gap-10 md:grid-cols-2 ${reverse ? 'md:[&>*:first-child]:order-2' : ''}`}>
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">{eyebrow}</p>
        <h3 className="mt-2 text-2xl font-bold text-gray-900">{title}</h3>
        <p className="mt-3 text-gray-600">{body}</p>
        <ul className="mt-4 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckIcon />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-50 to-white p-6 shadow-sm">
        <div className="space-y-3">
          <div className="h-3 w-1/3 rounded-full bg-brand-200" />
          <div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
            {bullets.map((b, i) => (
              <div key={b} className="flex items-center justify-between border-b border-gray-50 py-2 last:border-0">
                <span className="text-xs text-gray-500">{b}</span>
                <span
                  className={i % 2 === 0 ? 'h-2 rounded-full bg-brand-500' : 'h-2 rounded-full bg-brand-300'}
                  style={{ width: `${40 + ((i * 17) % 45)}px` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The closing call-to-action band, shared across marketing pages. */
export function FinalCTA(): ReactElement {
  return (
    <section className="bg-brand-700">
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <h2 className="text-3xl font-bold text-white">Ready to run payroll the easy way?</h2>
        <p className="mt-3 text-brand-100">Set up your company and run your first payroll today.</p>
        <Link
          to="/register"
          className="mt-8 inline-block rounded-lg bg-white px-7 py-3 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50"
        >
          Get started
        </Link>
      </div>
    </section>
  );
}
